<template>
	<div ref="container">
		<h3 class="project-revenue-forecast-title">
			{{ $t('project_revenue_forecast') }}
		</h3>
		<div class="project-revenue-forecast">
			<ProjectRevenueForecastHeadings
				:loading="isRevenueForecastLoading"
				:currency="forecastCurrency"
			/>

			<div class="project-revenue-forecast">
				<div
					class="project-revenue-forecast__data"
					:class="{
						'project-revenue-forecast__data--start-shadow':
							isForecastFirstMonthVisible,
						'project-revenue-forecast__data--end-shadow':
							isForecastLastMonthVisible,
					}"
				>
					<div class="project-revenue-forecast__data-container">
						<div
							ref="leftShadowWhenVisible"
							class="project-revenue-forecast__data-left-shadow"
						/>
						<SuiDataTable
							class="project-revenue-forecast__data-table"
							:col-definition="revenueRecognitionColumns"
							:hide-header="isRevenueForecastLoading"
							:loading="isRevenueForecastLoading"
						>
							<SuiDataTableRow>
								<SuiDataTableCell />
								<SuiDataTableCell
									v-for="period in forecastPeriods"
									:key="period.id"
									:align="SuiDataTableColumnAlign.RIGHT"
									class="project-revenue-forecast__cell"
								>
									<span>
										{{
											period.earnedRevenue !== null
												? formatAmount(
														Number(
															period.earnedRevenue
														)
													)
												: ''
										}}
									</span>
								</SuiDataTableCell>
							</SuiDataTableRow>
							<SuiDataTableRow>
								<SuiDataTableCell />
								<SuiDataTableCell
									v-for="period in forecastPeriods"
									:key="period.id"
									:align="SuiDataTableColumnAlign.RIGHT"
									class="project-revenue-forecast__cell project-revenue-forecast__cell--inputs"
								>
									<SuiNumberInput
										v-if="period.revenue !== null"
										v-model="period.revenueTemp"
										size="small"
										:allow-characters="['%']"
										:placeholder="
											getPlaceholderValue(
												period,
												RevenueFieldType.RECOGNIZED
											)
										"
										:formatted-value="
											getFormattedValue(
												period,
												RevenueFieldType.RECOGNIZED
											)
										"
										class="project-revenue-forecast__recognized-revenue-input"
										hide-clear-button
										data-testId="project-revenue-forecast-input"
										:align="SuiInputAlign.Right"
										allow-decimals
										@focus="
											onInputFocus(
												period.id,
												RevenueFieldType.RECOGNIZED
											)
										"
										@blur="
											saveUserInput(
												period.id,
												RevenueFieldType.RECOGNIZED
											)
										"
										@keydown="
											onKeydown(
												period.id,
												RevenueFieldType.RECOGNIZED,
												$event
											)
										"
									/>
									<template v-else>&nbsp;</template>
								</SuiDataTableCell>
							</SuiDataTableRow>
							<SuiDataTableRow>
								<SuiDataTableCell />
								<SuiDataTableCell
									v-for="period in forecastPeriods"
									:key="period.id"
									:align="SuiDataTableColumnAlign.RIGHT"
									class="project-revenue-forecast__cell project-revenue-forecast__cell--inputs"
								>
									<SuiNumberInput
										v-if="
											period.forecastedRevenue !== null &&
											period.revenueTemp.length === 0
										"
										v-model="period.forecastedRevenueTemp"
										size="small"
										:allow-characters="['%']"
										:placeholder="
											getPlaceholderValue(
												period,
												RevenueFieldType.FORECASTED
											)
										"
										:formatted-value="
											getFormattedValue(
												period,
												RevenueFieldType.FORECASTED
											)
										"
										class="project-revenue-forecast__recognized-revenue-input"
										hide-clear-button
										data-testId="project-revenue-forecast-input"
										:align="SuiInputAlign.Right"
										allow-decimals
										@focus="
											onInputFocus(
												period.id,
												RevenueFieldType.FORECASTED
											)
										"
										@blur="
											saveUserInput(
												period.id,
												RevenueFieldType.FORECASTED
											)
										"
										@keydown="
											onKeydown(
												period.id,
												RevenueFieldType.FORECASTED,
												$event
											)
										"
									/>
									<template v-else>&nbsp;</template>
								</SuiDataTableCell>
							</SuiDataTableRow>
							<SuiDataTableRow>
								<SuiDataTableCell />
								<SuiDataTableCell
									v-for="period in forecastPeriods"
									:key="period.id"
									:align="SuiDataTableColumnAlign.RIGHT"
									class="project-revenue-forecast__cell project-revenue-forecast__cell--totals"
								>
									<div
										class="project-revenue-forecast__highlighted"
									>
										{{ formatAmount(period.total) }}
									</div>
									<div
										v-if="quotedRevenue > 0"
										class="project-revenue-forecast__light"
									>
										{{
											formatNumber(
												period.totalPercentage,
												{
													decimalPrecision: 0,
												}
											)
										}}%
									</div>
								</SuiDataTableCell>
							</SuiDataTableRow>
						</SuiDataTable>
						<div
							ref="rightShadowWhenVisible"
							class="project-revenue-forecast__data-right-shadow"
						/>
					</div>
				</div>

				<ProjectRevenueForecastTotals :project-id="String(projectId)" />
			</div>
		</div>
	</div>
</template>
<script lang="ts" setup>
import { type InputHTMLAttributes, computed, nextTick, ref } from 'vue';
import { useFormatter, useTranslation } from '@/composables';
import { useMessage } from '@/features/Message';
import {
	SuiDataTable,
	SuiDataTableRow,
	SuiDataTableCell,
	SuiNumberInput,
	type SuiDataTableColumnType,
	SuiInputAlign,
	SuiDataTableColumnAlign,
} from '@developers/scoro-ui';
import ProjectRevenueForecastHeadings from './ProjectRevenueForecastHeadings.vue';
import ProjectRevenueForecastTotals from './ProjectRevenueForecastTotals.vue';
import {
	type PeriodWithAdditionalFields,
	useProjectRevenueForecast,
} from './useProjectRevenueForecast';
import { useProjectRevenueForecastTableSHadow } from './usProjectRevenueForecastTableShadow';
import {
	type Project,
	type RevenueForecastPeriod,
	useProjectRevenueForecastPeriodSaveMutation,
} from '@/generated/graphql';
import round from 'lodash/round';
import { format } from 'date-fns';
import { resetTime } from '@/utils/formatting';
import { KeyboardKey } from '@/constants/constants';
import { RevenueFieldType, type RevenueFieldTypeValue } from './constants';

const props = defineProps<{
	projectId: Project['id'];
}>();

const { t } = useTranslation();
const { addSuccessMessage, addErrorMessage } = useMessage();
const { formatNumber, formatMonthShortNoCurrentYear } = useFormatter();

const leftShadowWhenVisible = ref<HTMLDivElement>();
const rightShadowWhenVisible = ref<HTMLDivElement>();

const { isForecastFirstMonthVisible, isForecastLastMonthVisible } =
	useProjectRevenueForecastTableSHadow(
		leftShadowWhenVisible,
		rightShadowWhenVisible
	);

const focusedPeriodId = ref<RevenueForecastPeriod['id'] | null>(null);
const focusedFieldType = ref<RevenueFieldTypeValue | null>(null);

const onInputFocus = (
	periodId: RevenueForecastPeriod['id'],
	type: RevenueFieldTypeValue
) => {
	if (!periodId) {
		return;
	}
	focusedPeriodId.value = periodId;
	focusedFieldType.value = type;
};

const {
	loading: isRevenueForecastLoading,
	quotedRevenue,
	forecastCurrency,
	revenueForecast,
	forecastPeriods,
	formatAmount,
} = useProjectRevenueForecast(
	String(props.projectId),
	focusedPeriodId,
	focusedFieldType
);

const { mutate: saveRevenueForecastPeriod } =
	useProjectRevenueForecastPeriodSaveMutation({});

const revenueRecognitionColumns = computed<SuiDataTableColumnType[]>(() => {
	const maxColumnsToFit = 8;
	let colWidth: number | undefined = undefined;

	if (
		revenueForecast.value?.periods &&
		revenueForecast.value?.periods?.length <= maxColumnsToFit
	) {
		colWidth = 20;
	}

	const monthLabels =
		revenueForecast.value?.periods.map((el, index) => ({
			id: String(index),
			title: formatMonthShortNoCurrentYear(el.period),
			width: colWidth ? `${colWidth}rem` : undefined,
			align: SuiDataTableColumnAlign.RIGHT,
			isMarked: el.period === format(resetTime(new Date()), 'yyyy-MM-01'),
		})) || [];

	return [
		{
			id: 'empty-placeholder',
			title: '',
		},
		...monthLabels,
	];
});

const getPlaceholderValue = (
	period: PeriodWithAdditionalFields,
	type: RevenueFieldTypeValue
) => {
	const calculatedValue =
		type === RevenueFieldType.RECOGNIZED
			? period.revenue
			: period.forecastedRevenue;

	if (typeof calculatedValue === 'number' && !period.isRevenueSetByUser) {
		return formatAmount(calculatedValue);
	}

	return '0';
};

const getFormattedValue = (
	period: PeriodWithAdditionalFields,
	type: RevenueFieldTypeValue
) => {
	const userInputValue =
		type === RevenueFieldType.RECOGNIZED
			? period.revenueTemp
			: period.forecastedRevenueTemp;

	return userInputValue.length
		? formatAmount(Number(userInputValue))
		: undefined;
};

const saveUserInput = async (
	periodId: RevenueForecastPeriod['id'] | undefined,
	type: RevenueFieldTypeValue,
	forceSave = false
) => {
	await nextTick();

	const updatedPeriod = forecastPeriods.value.find(
		(period) => period.id === periodId
	);

	if (periodId && updatedPeriod) {
		// Cast input values to number if there is any input
		// Empty value is also allowed
		if (updatedPeriod?.revenueTemp.length > 0) {
			updatedPeriod.revenueTemp = String(
				round(Number(updatedPeriod?.revenueTemp), 2)
			);
		}

		if (updatedPeriod?.forecastedRevenueTemp.length > 0) {
			updatedPeriod.forecastedRevenueTemp = String(
				round(Number(updatedPeriod?.forecastedRevenueTemp), 2)
			);
		}

		// If value is the same as original, no point triggering save
		// Unless user presses Enter which forces save
		if (
			type === RevenueFieldType.RECOGNIZED &&
			((String(updatedPeriod.revenue) === updatedPeriod.revenueTemp &&
				updatedPeriod.isRevenueSetByUser) ||
				(!updatedPeriod.isRevenueSetByUser &&
					updatedPeriod.revenueTemp === '')) &&
			!forceSave
		) {
			return;
		}

		if (
			type === RevenueFieldType.FORECASTED &&
			(String(updatedPeriod.forecastedRevenue) ===
				updatedPeriod.forecastedRevenueTemp ||
				(!updatedPeriod.isRevenueSetByUser &&
					updatedPeriod.forecastedRevenueTemp === '')) &&
			!forceSave
		) {
			return;
		}

		const recognizedRevenue =
			forceSave &&
			updatedPeriod.revenueTemp === '' &&
			!updatedPeriod.isRevenueSetByUser
				? String(updatedPeriod.revenue)
				: updatedPeriod.revenueTemp;
		const forecastedRevenue =
			forceSave &&
			updatedPeriod.forecastedRevenueTemp === '' &&
			!updatedPeriod.isRevenueSetByUser
				? String(updatedPeriod.forecastedRevenue)
				: updatedPeriod.forecastedRevenueTemp;

		// Back-end expects either number or null to reset field to be auto-calculated
		// When we save recognized or forecasted value, the other value should be null
		const res = await saveRevenueForecastPeriod({
			input: {
				id: periodId,
				recognizedRevenue:
					type === RevenueFieldType.RECOGNIZED
						? recognizedRevenue.length > 0
							? Number(recognizedRevenue)
							: null
						: null,
				forecastedRevenue:
					type === RevenueFieldType.FORECASTED
						? forecastedRevenue.length > 0
							? Number(forecastedRevenue)
							: null
						: null,
			},
		});

		if (res?.errors) {
			addErrorMessage({
				message: t('project_forecast_save_failed'),
			});

			return;
		}

		addSuccessMessage({
			message: t('project_forecast_save_success'),
		});
	}
};

const onKeydown = (
	periodId: RevenueForecastPeriod['id'] | undefined,
	type: RevenueFieldTypeValue,
	event: KeyboardEvent
) => {
	// Force save
	if (event.key === KeyboardKey.ENTER) {
		saveUserInput(periodId, type, true);
	}
	// Calculate percentage value
	else if (event.key === '%') {
		const input = event.target as InputHTMLAttributes;
		const percents = Math.round(Number(input.value));

		if (quotedRevenue.value > 0 && percents > 0 && percents <= 100) {
			const period = forecastPeriods.value.find(
				(period) => period.id === periodId
			);

			if (period) {
				const calculatedValue = String(
					round((quotedRevenue.value / 100) * percents, 2)
				);

				if (type === RevenueFieldType.FORECASTED) {
					period.forecastedRevenueTemp = calculatedValue;
				} else {
					period.revenueTemp = calculatedValue;
				}
			}
		}

		event.preventDefault();
		return false;
	}

	return true;
};
</script>
<style lang="scss">
.project-revenue-forecast-title {
	padding: 1.125rem 0 1rem;
}

.project-revenue-forecast {
	flex-grow: 1;
	flex-shrink: 1;
	display: flex;
	overflow: hidden;
	justify-content: flex-end;

	&__highlighted {
		color: $neutral-80;
		font-weight: $scoro-fw-semi-bold;
	}

	&__light {
		color: $neutral-70;
		font-size: 1.375rem;
	}

	&__data-table {
		.sui-data-table-col:last-child:not(
				.project-revenue-forecast__cell--inputs
			) {
			padding-right: 1.5rem !important;
		}
	}

	.project-revenue-forecast__cell {
		line-height: 4rem;

		span {
			display: inline-block;
		}

		&--totals {
			line-height: 2rem;
			padding-top: 2.5rem;
		}

		&--inputs {
			padding-right: calc(0.5rem - 1px) !important;
			padding-left: calc(0.5rem - 1px) !important;
		}
	}

	&__data {
		--sui-data-table-side-spacing: 1.5rem;

		position: relative;
		overflow: hidden;
		flex-grow: 1;

		&--start-shadow::before,
		&--end-shadow::after {
			box-shadow: $scoro-box-shadow-larger;
			content: ' ';
			height: 100%;
			position: absolute;
			width: 1.25rem;
			top: 0;
		}

		&--start-shadow::before {
			right: 100%;
		}

		&--end-shadow::after {
			left: 100%;
		}

		.sui-data-table-header__col {
			color: $neutral-90;
			font-weight: $scoro-fw-medium;
		}
	}

	&__data-container {
		display: flex;
		overflow-x: auto;

		.sui-data-table-wrapper {
			width: fit-content;
			flex-shrink: 0;
			overflow: visible;
			min-width: 100%;
		}
	}

	&__data-left-shadow,
	&__data-right-shadow {
		width: 1rem;
		min-width: 1rem;
		height: 100%;
		min-height: 11rem;
	}

	&__data-left-shadow {
		margin-right: -2rem;
	}

	&__data-right-shadow {
		margin-left: -1rem;
	}

	&__recognized-revenue-input {
		min-width: 12rem;
		max-width: 20rem;
		margin-left: auto;

		.sui-input__field {
			width: 100%;
		}
	}
}
</style>
